import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { FiCheck, FiCopy } from 'react-icons/fi'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import blogs from '../../content/blogs/registry'
import {
  logBlogOpen,
  logBlogScrollDepth,
  logBlogReadTime,
} from '../../supabaseTracker'
import './BlogPost.css'

const SCROLL_MILESTONES = [25, 50, 75, 100]

const getScrollDepthPct = () => {
  const docHeight =
    document.documentElement.scrollHeight - window.innerHeight
  if (docHeight <= 0) return 100
  return Math.min(100, Math.round((window.scrollY / docHeight) * 100))
}

// Simple frontmatter parser (no Node.js deps)
function parseFrontmatter(text) {
  const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/)
  if (!match) return { attributes: {}, body: text }

  const yamlBlock = match[1]
  const body = match[2]
  const attributes = {}

  yamlBlock.split('\n').forEach((line) => {
    const colonIdx = line.indexOf(':')
    if (colonIdx === -1) return
    const key = line.slice(0, colonIdx).trim()
    let val = line.slice(colonIdx + 1).trim()

    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1)
    }
    if (val.startsWith('[') && val.endsWith(']')) {
      try {
        val = JSON.parse(val)
      } catch (e) {
        // keep as string
      }
    }
    attributes[key] = val
  })

  return { attributes, body }
}

const BlogMarkdownCodeBlock = ({ language, children, ...props }) => {
  const [copied, setCopied] = useState(false)
  const code = String(children).replace(/\n$/, '')
  const isPrompt = language === 'text'
  const copyLabel = isPrompt ? 'Copy prompt' : 'Copy code'

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="blog-post__code-block">
      <div className="blog-post__code-header">
        <span className="blog-post__code-lang">{language}</span>
        <button
          type="button"
          className={`blog-post__code-copy${
            copied ? ' blog-post__code-copy--copied' : ''
          }`}
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : copyLabel}
          title={copied ? 'Copied' : copyLabel}
        >
          {copied ? <FiCheck aria-hidden /> : <FiCopy aria-hidden />}
        </button>
      </div>
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: '0 0 12px 12px',
          fontSize: '0.88rem',
          padding: '1.5em',
        }}
        {...props}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

const BlogPost = () => {
  const { slug } = useParams()
  const [content, setContent] = useState('')
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)

  const blogEntry = blogs.find((b) => b.slug === slug)

  useEffect(() => {
    if (!blogEntry) {
      setLoading(false)
      return
    }

    fetch(blogEntry.markdownUrl)
      .then((res) => res.text())
      .then((text) => {
        const { attributes, body } = parseFrontmatter(text)
        setMeta(attributes)
        setContent(body)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug, blogEntry])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  useEffect(() => {
    if (slug) logBlogOpen(slug)
  }, [slug])

  // Scroll depth + active time-on-page for this blog
  useEffect(() => {
    if (!slug || loading || !blogEntry) return

    const state = {
      activeMs: 0,
      activeStartedAt:
        document.visibilityState === 'visible' ? Date.now() : null,
      maxScroll: 0,
      firedMilestones: new Set(),
      flushed: false,
    }

    const pauseActive = () => {
      if (state.activeStartedAt == null) return
      state.activeMs += Math.max(0, Date.now() - state.activeStartedAt)
      state.activeStartedAt = null
    }

    const resumeActive = () => {
      if (state.activeStartedAt != null) return
      if (document.visibilityState === 'visible') {
        state.activeStartedAt = Date.now()
      }
    }

    const takeActiveMs = () => {
      pauseActive()
      return state.activeMs
    }

    const maybeFireMilestones = (pct) => {
      SCROLL_MILESTONES.forEach((milestone) => {
        if (pct >= milestone && !state.firedMilestones.has(milestone)) {
          state.firedMilestones.add(milestone)
          logBlogScrollDepth(slug, milestone)
        }
      })
    }

    const onScroll = () => {
      const pct = getScrollDepthPct()
      state.maxScroll = Math.max(state.maxScroll, pct)
      maybeFireMilestones(pct)
    }

    const flushReadTime = (keepalive = false) => {
      if (state.flushed) return
      const duration_ms = takeActiveMs()
      if (duration_ms < 1000) return
      state.flushed = true
      logBlogReadTime(slug, duration_ms, state.maxScroll, { keepalive })
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushReadTime(true)
      } else {
        // Allow a fresh read-time pulse if they return to the tab
        state.flushed = false
        state.activeMs = 0
        resumeActive()
      }
    }

    const onPageHide = () => flushReadTime(true)

    // Short articles that fit in viewport still count as fully scrolled
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      flushReadTime(false)
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [slug, loading, blogEntry])

  if (loading) {
    return (
      <div className="blog-post__loading">
        <div className="blog-post__spinner" />
      </div>
    )
  }

  if (!blogEntry || !meta) {
    return (
      <div className="blog-post__not-found">
        <h1>Blog not found</h1>
        <Link to="/blogs" className="blog-post__back-link">
          ← Back to all blogs
        </Link>
      </div>
    )
  }

  const resolveImage = (src) => {
    if (blogEntry.images && blogEntry.images[src]) {
      return blogEntry.images[src]
    }
    return src
  }

  return (
    <article className="blog-post">
      <header className="blog-post__header">
        <Link to="/blogs" className="blog-post__back-link">
          ← All Blogs
        </Link>

        {blogEntry.thumbnail && (
          <div className="blog-post__cover">
            <img
              src={blogEntry.thumbnail}
              alt={meta.title}
              className="blog-post__cover-img"
            />
          </div>
        )}

        <div className="blog-post__tags">
          {meta.tags &&
            meta.tags.map((tag) => (
              <span key={tag} className="blog-post__tag">
                {tag}
              </span>
            ))}
        </div>

        <h1 className="blog-post__title">{meta.title}</h1>

        <div className="blog-post__meta">
          <span className="blog-post__author">{meta.author}</span>
          <span className="blog-post__dot">·</span>
          <span className="blog-post__date">
            {new Date(meta.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
          {meta.readTime && (
            <>
              <span className="blog-post__dot">·</span>
              <span className="blog-post__read-time">{meta.readTime}</span>
            </>
          )}
        </div>
      </header>

      <div className="blog-post__content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ src, alt }) => (
              <figure className="blog-post__figure">
                <img
                  src={resolveImage(src)}
                  alt={alt}
                  className="blog-post__image"
                  loading="lazy"
                />
                {alt && (
                  <figcaption className="blog-post__figcaption">
                    {alt}
                  </figcaption>
                )}
              </figure>
            ),

            code: ({ inline, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '')
              if (!inline && match) {
                return (
                  <BlogMarkdownCodeBlock language={match[1]} {...props}>
                    {children}
                  </BlogMarkdownCodeBlock>
                )
              }
              return (
                <code className="blog-post__inline-code" {...props}>
                  {children}
                </code>
              )
            },

            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="blog-post__link"
              >
                {children}
              </a>
            ),

            blockquote: ({ children }) => (
              <blockquote className="blog-post__blockquote">
                {children}
              </blockquote>
            ),

            ul: ({ children }) => (
              <ul className="blog-post__ul">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="blog-post__ol">{children}</ol>
            ),

            h2: ({ children }) => {
              const id = String(children)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '')
              return (
                <h2 id={id} className="blog-post__h2">
                  {children}
                </h2>
              )
            },
            h3: ({ children }) => {
              const id = String(children)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '')
              return (
                <h3 id={id} className="blog-post__h3">
                  {children}
                </h3>
              )
            },
            h4: ({ children }) => (
              <h4 className="blog-post__h4">{children}</h4>
            ),

            p: ({ children }) => (
              <p className="blog-post__p">{children}</p>
            ),

            hr: () => <hr className="blog-post__hr" />,

            strong: ({ children }) => (
              <strong className="blog-post__strong">{children}</strong>
            ),

            table: ({ children }) => (
              <div className="blog-post__table-wrap">
                <table className="blog-post__table">{children}</table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="blog-post__thead">{children}</thead>
            ),
            th: ({ children }) => (
              <th className="blog-post__th">{children}</th>
            ),
            td: ({ children }) => (
              <td className="blog-post__td">{children}</td>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      <footer className="blog-post__footer">
        <Link to="/blogs" className="blog-post__back-link">
          ← Back to all blogs
        </Link>
      </footer>
    </article>
  )
}

export default BlogPost
